/**
 * GST calculation utility — dynamic based on organization state.
 *
 * Under Indian GST law:
 * - Same state as supplier (Maharashtra) → CGST 9% + SGST 9%
 * - Different state → IGST 18%
 * - No state info → IGST 18% (safe default)
 *
 * @param {number} base - Base amount in rupees
 * @param {string} gstConfig - 'none' | 'applicable' | 'exclusive_18' | 'split_9_9'
 *   'none' = no GST; anything else = GST applicable (type determined by state)
 * @param {string|null} orgState - Organization's state (e.g., 'Maharashtra')
 * @param {string} supplierState - Supplier's state (default: 'Maharashtra')
 * @returns {{ gstAmount: number, cgstAmount: number, sgstAmount: number, igstAmount: number, totalAmount: number, gstLabel: string, gstType: string }}
 */
export const calculateGST = (base, gstConfig = 'applicable', orgState = null, supplierState = 'Maharashtra') => {
  if (gstConfig === 'none') {
    return { gstAmount: 0, cgstAmount: 0, sgstAmount: 0, igstAmount: 0, totalAmount: base, gstLabel: '', gstType: 'none' };
  }

  // All non-'none' values mean GST applies; determine type by state
  const normalizedOrgState = (orgState || '').toLowerCase().trim();
  const normalizedSupplierState = (supplierState || 'Maharashtra').toLowerCase().trim();

  // Same state → CGST + SGST (intra-state supply)
  if (normalizedOrgState && normalizedOrgState === normalizedSupplierState) {
    const cgstAmount = Math.round(base * 0.09 * 100) / 100;
    const sgstAmount = Math.round(base * 0.09 * 100) / 100;
    const totalAmount = Math.round((base + cgstAmount + sgstAmount) * 100) / 100;
    return {
      gstAmount: cgstAmount + sgstAmount,
      cgstAmount,
      sgstAmount,
      igstAmount: 0,
      totalAmount,
      gstLabel: 'CGST 9% + SGST 9%',
      gstType: 'cgst_sgst',
    };
  }

  // Different state or unknown → IGST (inter-state supply)
  const gstAmount = Math.round(base * 0.18 * 100) / 100;
  const totalAmount = Math.round((base + gstAmount) * 100) / 100;
  return {
    gstAmount,
    cgstAmount: 0,
    sgstAmount: 0,
    igstAmount: gstAmount,
    totalAmount,
    gstLabel: 'IGST 18%',
    gstType: 'igst',
  };
};

/**
 * Indian states and union territories for dropdown.
 */
export const INDIAN_STATES = [
  'Andhra Pradesh',
  'Arunachal Pradesh',
  'Assam',
  'Bihar',
  'Chhattisgarh',
  'Delhi',
  'Goa',
  'Gujarat',
  'Haryana',
  'Himachal Pradesh',
  'Jammu and Kashmir',
  'Jharkhand',
  'Karnataka',
  'Kerala',
  'Ladakh',
  'Madhya Pradesh',
  'Maharashtra',
  'Manipur',
  'Meghalaya',
  'Mizoram',
  'Nagaland',
  'Odisha',
  'Punjab',
  'Rajasthan',
  'Sikkim',
  'Tamil Nadu',
  'Telangana',
  'Tripura',
  'Uttar Pradesh',
  'Uttarakhand',
  'West Bengal',
  'Andaman and Nicobar Islands',
  'Chandigarh',
  'Dadra and Nagar Haveli and Daman and Diu',
  'Lakshadweep',
  'Puducherry',
];